/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { ListItemCustom, Link, List } from '@ui5/webcomponents-react';
import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '@/hooks';
import { loadingActions } from '@/store/loadingSlice';
import axiosInstance from '@/utils/axiosInstance';
import { useTranslations } from '@/hooks/useTranslations';
import { ThemingParameters } from '@ui5/webcomponents-react-base';

const HelpComponent: React.FC<any> = () => {
    const [quickLink, setQuickLink] = useState<any>();
    const [leftColumnLinks, setLeftColumnLinks] = useState<any>([]);
    const [rightColumnLinks, setRightColumnLinks] = useState<any>([]);
    const dispatch = useAppDispatch();
    const { t } = useTranslations();
    const fetchHelpLinkData = async () => {
        dispatch(loadingActions.startLoading());
        try {
            const response = await axiosInstance.get(`/helpLinks/`);
            if (response.data) {
                const data: any = response.data;
                if (data.length > 0) return data.sort((helpLink1: any, helpLink2: any) => helpLink1.displayOrder - helpLink2.displayOrder);
                else {
                    throw new Error('Network response was not ok');
                }
            } else {
                throw new Error('Network response was not ok');
            }
        } catch (error) {
            console.error('Error loading JSON data:', error);
        } finally {
            dispatch(loadingActions.stopLoading());
        }
    };

    useEffect(() => {
        (async () => {
            const allHelpLinksForFlowId = await fetchHelpLinkData();
            setQuickLink(allHelpLinksForFlowId?.find((ele: any) => ele.displayOrder === 1));
            setLeftColumnLinks(allHelpLinksForFlowId?.filter((item: any) => item?.displayOrder % 2 === 0 && item.displayOrder !== 1));
            setRightColumnLinks(allHelpLinksForFlowId?.filter((item: any) => item?.displayOrder % 2 !== 0 && item.displayOrder !== 1));
        })();
    }, []);
    return (
        <section
            data-testId="help-component"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around' }}
        >
            <div style={{ textAlign: 'center', paddingBottom: ThemingParameters.sapContent_Padding_M }}>
                <h2 style={{ marginBottom: '10px' }}>Need Help?</h2>
                <p style={{ marginBottom: '10px' }}>Find the resources to answer your question below.</p>
                <Link
                    data-testId="quicklink"
                    design="Emphasized"
                    href={quickLink?.linkAddress}
                    target="_blank"
                    style={{ textDecoration: 'underline' }}
                    rel="noopener noreferrer"
                >
                    {t(quickLink?.translationLinkTextKey)}
                </Link>
            </div>
            <div style={{ display: 'flex' }}>
                <List separators="None">
                    {leftColumnLinks?.length > 0 &&
                        leftColumnLinks.map((helpLink: any) => {
                            return (
                                <ListItemCustom key={helpLink?.linkAddress} data-testId="left-column-link">
                                    <Link
                                        rel="noopener noreferrer"
                                        design="Default"
                                        href={helpLink?.linkAddress}
                                        target="_blank"
                                        style={{ textDecoration: 'underline' }}
                                    >
                                        {t(helpLink?.translationLinkTextKey)}
                                    </Link>
                                </ListItemCustom>
                            );
                        })}
                </List>
                <List separators="None">
                    {rightColumnLinks?.length > 0 &&
                        rightColumnLinks.map((helpLink: any) => {
                            return (
                                <ListItemCustom key={helpLink?.linkAddress} data-testId="right-column-link">
                                    <Link
                                        rel="noopener noreferrer"
                                        design="Default"
                                        href={helpLink?.linkAddress}
                                        target="_blank"
                                        style={{ textDecoration: 'underline' }}
                                    >
                                        {t(helpLink?.translationLinkTextKey)}
                                    </Link>
                                </ListItemCustom>
                            );
                        })}
                </List>
            </div>
            <div style={{ textAlign: 'center', marginTop: '15px', marginBottom: '15px' }}>
                <p>
                    To reach out to support, please create a ticket with this component in SAP for Me: <b>BNS-ARI-FRE</b>
                </p>
                {/* <Button design="Emphasized" onClick={() => window.location.href = supportDl}>Contact Us</Button> */}
            </div>
        </section>
    );
};

export default HelpComponent;
